import React, { useState, useEffect, useRef } from "react";
import {
  TableContainer,
  Paper,
  Typography,
  Box,
  PaginationItem,
} from "@mui/material";
import { api, get } from "../../../../utils/api";
import { styled } from "@mui/material/styles";
import DateRangePickerComponent from "../../Roles and Permissions/Filters/Date";
import PaginationBox from "../../../../Components/Masters/Pagination";
import DeleteDialog from "../../../../Components/Masters/DeleteDialog";
import ColumnHeadings from "../../../../Components/Masters/ColumnHeadings";
import MenuActions from "../../../../Components/Masters/MenuActions";
import TableContent from "../../../../Components/Masters/TableContent";
import FilterDrawer from "../../../../Components/Masters/FilterDrawer";
import TopMenu from "../../../../Components/Masters/TopMenu";
import MessagePopup from "../../../../Components/Masters/MessagePopup";
import TableHeading from "../../../../Components/Masters/TableHeading";
import MenuIcon from "../../../../Components/Masters/MenuIcon";
import MultiSelect from "../../Roles and Permissions/Filters/MultiSelect";
import AddInvestment from "./AddInvestment";
import ViewInvestment from "./ViewInvestment";
import NoDataFound from "../../../../Components/Masters/NoDataFound";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import MastersSidebar from "../../../../Layout/MastersSidebar";
import Unauthorized from "../../../../Components/Unauthorized/Unauthorized";

const StyledPaginationItem = styled(PaginationItem)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: "green",
    color: "#fff",
    fontWeight: "bold",
  },
  "&:hover": {
    backgroundColor: "#ededed",
  },
}));

const formInputData = [
  { fieldName: "investmentType", title: "Investment Type" },
  {
    fieldName: "investeeProjectName",
    title: "Investee/Project Name",
    placeholder: "Alphanumeric",
  },
  { fieldName: "sector", title: "Sector" },
  {
    fieldName: "countryOfOperation",
    title: "Country of operation",
    placeholder: "Tap to select",
    type: "select",
  },
  {
    fieldName: "equityHoldingPercent",
    title: "Equity Holding (%)",
    placeholder: "Numeric box",
  },
];

const viewInputData = [
  { fieldName: "investmentType", title: "Investment Type" },
  { fieldName: "investeeProjectName", title: "Investee/Project Name" },
  { fieldName: "sector", title: "Sector" },
  { fieldName: "countryOfOperation", title: "Country of operation" },
  { fieldName: "equityHoldingPercent", title: "Equity Holding (%)" },
];

const Investment = () => {
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addData, setAddData] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    Date: false,
    "Investment Type": false,
    Country: false,
  });
  const [viewData, setViewData] = useState(false);
  const [element, setElement] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("Date");
  const [drawerFilterOpen, setDrawerFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [uniqueValues, setUniqueValues] = useState({});
  const [errors, setErrors] = useState({
    Date: false,
    "Investment Type": false,
    Country: false,
  });
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [selectedFields, setSelectedFields] = useState({
    investmentType: [],
    countryOfOperation: [],
  });
  const [tempFilter, setTempFilter] = useState({
    investmentType: [],
    countryOfOperation: [],
    startDate: null,
    endDate: null,
  });

  const filters = ["Date", "Investment Type", "Country"];

  const filtersToApply = {
    startDate: tempFilter?.startDate ? tempFilter?.startDate.format("YYYY-MM-DD") : "",
    endDate: tempFilter?.endDate ? tempFilter?.endDate.format("YYYY-MM-DD") : "",
    investmentType: tempFilter?.investmentType?.join(",") || "",
    countryOfOperation: tempFilter?.countryOfOperation?.join(",") || "",
  };

  const fetchUniqueValues = async () => {
    try {
      const response = await get(`/master/investmentMaster/filter`);
      return {
        investmentType: response?.data?.data?.investmentType || [],
        countryOfOperation: response?.data?.data?.countryOfOperation || [],
      };
    } catch (error) {
      return {};
    }
  };

  const updatePage = () => {
    if (page > 1 && data.length === 1) {
      setPage(page - 1);
    }
    fetchData();
  };

  const fetchData = async (filtersArg = isFiltersApplied ? filtersToApply : {}) => {
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(rowsPerPage),
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...Object.fromEntries(
          Object.entries(filtersArg).filter(
            ([, v]) => v !== "" && v !== undefined && v !== null
          )
        ),
      }).toString();

      const response = await get(`/master/investmentMasters?${queryParams}`);
      setData(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
      const uniqueVals = await fetchUniqueValues();
      setUniqueValues(uniqueVals);
    } catch (error) {
      console.error("Error fetching investment master:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, sortConfig]);

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setElement(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setAddData(true);
    setViewData(false);
  };

  const handleView = () => {
    handleMenuClose();
    setViewData(true);
    setAddData(false);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchData(filtersToApply);
    setStartDate(tempFilter?.startDate);
    setEndDate(tempFilter?.endDate);
    setSelectedFields({
      investmentType: tempFilter?.investmentType || [],
      countryOfOperation: tempFilter?.countryOfOperation || [],
    });
    setIsFiltersApplied(true);
    handleAppliedFilters();
    setDrawerFilterOpen(false);
  };

  const handleResetFilters = async () => {
    setTempFilter({
      startDate: null,
      endDate: null,
      investmentType: [],
      countryOfOperation: [],
    });
    setStartDate(null);
    setEndDate(null);
    setSelectedFields({
      investmentType: [],
      countryOfOperation: [],
    });
    setPage(1);
    await fetchData({});
    setErrors({});
    setAppliedFilters({});
    setIsFiltersApplied(false);
    setDrawerFilterOpen(false);
  };

  const handleAppliedFilters = () => {
    const isDateApplied = tempFilter?.startDate || tempFilter?.endDate ? true : false;
    const isTypeApplied = tempFilter?.investmentType?.length > 0 ? true : false;
    const isCountryApplied = tempFilter?.countryOfOperation?.length > 0 ? true : false;

    setIsFiltersApplied(isDateApplied || isTypeApplied || isCountryApplied);

    setAppliedFilters((prev) => ({
      ...prev,
      Date: isDateApplied,
      "Investment Type": isTypeApplied,
      Country: isCountryApplied,
    }));
  };

  const handleSort = (column, direction) => {
    setSortConfig({ key: column, direction });
  };

  const handleFilterDrawer = () => {
    setTempFilter({
      startDate: startDate,
      endDate: endDate,
      investmentType: selectedFields.investmentType,
      countryOfOperation: selectedFields.countryOfOperation,
    });
    setDrawerFilterOpen(false);
  };

  const columnHeadings = [
    { fieldName: "createdAt", title: "Date" },
    { fieldName: "investmentType", title: "Investment Type" },
    { fieldName: "investeeProjectName", title: "Investee/Project Name" },
    { fieldName: "sector", title: "Sector" },
    { fieldName: "countryOfOperation", title: "Country" },
  ];

  const renderSelectedFilter = () => {
    switch (selectedFilter) {
      case "Date":
        return (
          <DateRangePickerComponent
            startDate={tempFilter?.startDate}
            endDate={tempFilter?.endDate}
            setStartDate={(date) =>
              setTempFilter((prev) => ({ ...prev, startDate: date }))
            }
            setEndDate={(date) =>
              setTempFilter((prev) => ({ ...prev, endDate: date }))
            }
            setErrors={setErrors}
            errors={errors.Date}
          />
        );
      case "Investment Type":
        return (
          <MultiSelect
            title="Investment Type"
            selectedFields={tempFilter}
            setSelectedFields={(updater) => {
              setTempFilter((prev) => {
                const newValue = typeof updater === "function" ? updater(prev) : updater;
                return {
                  ...prev,
                  investmentType: newValue.investmentType || newValue,
                };
              });
            }}
            errors={errors["Investment Type"]}
            setErrors={setErrors}
            field="investmentType"
            data={uniqueValues?.investmentType}
          />
        );
      case "Country":
        return (
          <MultiSelect
            title="Country"
            selectedFields={tempFilter}
            setSelectedFields={(updater) => {
              setTempFilter((prev) => {
                const newValue = typeof updater === "function" ? updater(prev) : updater;
                return {
                  ...prev,
                  countryOfOperation: newValue.countryOfOperation || newValue,
                };
              });
            }}
            errors={errors.Country}
            setErrors={setErrors}
            field="countryOfOperation"
            data={uniqueValues?.countryOfOperation}
          />
        );
      default:
        return null;
    }
  };

  const tableContainerRef = useRef(null);
  const [isShortContent, setIsShortContent] = useState(false);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const tableHeight = container.scrollHeight;
      const screenHeight = window.innerHeight;
      setIsShortContent(tableHeight < screenHeight / 1.5);
    }
  }, [data]);

  const getDateFieldForColumn = (item, el) => {
    if (el.fieldName === "createdAt") return item.createdAt;
    return false;
  };

  return (
    <MainDashboardLayout sidebar={<MastersSidebar />}>
      <Unauthorized requiredModule="Setup | Master">
        <Box sx={{ backgroundColor: "#ededed", width: "100%" }}>
          <MessagePopup {...{ snackOpen, setsnackOpen }} />
          <AddInvestment
            id={element ? element._id : ""}
            addData={addData}
            setAddData={setAddData}
            element={element}
            addUrl={"/master/investmentMasters"}
            updateUrl={"/master/investmentMasters"}
            fetchData={fetchData}
            setsnackOpen={setsnackOpen}
            formInputData={formInputData}
          />
          <ViewInvestment
            data={element}
            viewData={viewData}
            setViewData={setViewData}
            inputData={viewInputData}
            handleEdit={handleEdit}
          />
          {deleteDialog && (
            <DeleteDialog
              fetchData={updatePage}
              {...{
                deleteDialog,
                setDeleteDialog,
                id: element?._id,
                url: `${api}/master/investmentMasters`,
                method: "delete",
                setsnackOpen,
              }}
            />
          )}
          {drawerFilterOpen && (
            <FilterDrawer
              {...{
                renderSelectedFilter,
                drawerFilterOpen,
                setDrawerFilterOpen,
                handleApplyFilters,
                handleFilterDrawer,
                setSelectedFilter,
                handleResetFilters,
                selectedFilter,
                filters,
                appliedFilters,
                selectedFields,
                setSelectedFields,
              }}
            />
          )}
          <TopMenu
            setData={setElement}
            setAddData={setAddData}
            {...{
              heading: "Scope 3 - Investment master",
              setDrawerFilterOpen,
              filterApplied: isFiltersApplied,
            }}
          />
          <TableContainer component={Paper} ref={tableContainerRef} sx={{ maxWidth: "100%" }}>
            <Box>
              <TableHeading heading={"Investment master"} />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "12% 17% 17% 20% 25% 8%",
                  borderBottom: "1px solid #ddd",
                  borderTop: "1px solid #ddd",
                }}
              >
                {columnHeadings?.map((el) => (
                  <ColumnHeadings
                    key={el.fieldName}
                    {...{
                      title: el.title,
                      key: el.title,
                      handleSort,
                      field: el.fieldName,
                      sortConfig,
                    }}
                  />
                ))}
                <Typography sx={{ fontWeight: "bold", padding: "8px 12px", textAlign: "center" }}>
                  Action
                </Typography>
              </Box>
              {data.length === 0 ? (
                <NoDataFound />
              ) : (
                data?.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "12% 17% 17% 20% 25% 8%",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    {columnHeadings?.map((el) => (
                      <TableContent
                        key={el.fieldName}
                        {...{
                          fieldData: item[el.fieldName],
                          dateField: getDateFieldForColumn(item, el),
                        }}
                      />
                    ))}
                    <MenuIcon {...{ handleMenuClick, field: item }} />
                  </Box>
                ))
              )}
            </Box>
          </TableContainer>
          {totalPages > 1 && (
            <PaginationBox
              isShortContent={isShortContent}
              {...{ totalPages, page, handleChangePage, StyledPaginationItem }}
            />
          )}
          <MenuActions
            {...{
              id: element?._id,
              handleView,
              handleEdit,
              handleDelete,
              anchorEl,
              handleMenuClose,
            }}
          />
        </Box>
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default Investment;
